import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";
import { TONE_TINT, type Tone } from "./tone";

type ListRowProps = {
  icon: LucideIcon;
  tone: Tone;
  title: string;
  /** Beside the title — a status badge. */
  badge?: ReactNode;
  /** The second line, muted. */
  detail?: ReactNode;
  /** The small third line — who and when. */
  meta?: ReactNode;
  to?: string;
};

/** One row of a widget list: tinted icon, title, detail and meta — the whole row a link. */
export function ListRow({ icon: Icon, tone, title, badge, detail, meta, to }: ListRowProps) {
  const body = (
    <>
      <div
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
          TONE_TINT[tone],
        )}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium">{title}</p>
          {badge}
        </div>
        {detail && <p className="truncate text-sm text-muted-foreground">{detail}</p>}
        {meta && <p className="text-xs text-muted-foreground">{meta}</p>}
      </div>
    </>
  );
  const className = "flex items-start gap-3 rounded-lg border p-3";
  return to ? (
    <Link to={to} className={cn(className, "transition-colors hover:bg-accent/50")}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}
